import CredentialsProvider from "next-auth/providers/credentials"

import { SESSION_MAX_AGE } from "@/constants/session"
import { getGqlClient, gql } from "@/graphql"
import { getLocaleStrings } from "@/providers/localeStrings/server"
import { Authsignal } from "@/services/authsignal"
import { getSignInSchema } from "@/utils/validation"

import { setAdmCookies } from "../cookies/setAdmCookies"
import { setCustomerCookies } from "../cookies/setCustomerCookies"

export const isIdmUser = (role) => {
  const idmRoles = [
    "IDM_USER",
    "IDM_DISPONENT",
    "IDM_LEAD",
    "IDM_THEKE",
    "IDM_SPRINGER",
    "IDM_SPRINGER_LEAD",
    "IDM_PROCUREMENT",
  ]
  return idmRoles.includes(role)
}

export const isIdmSpringer = (role) => {
  const idmSpringerRoles = ["IDM_SPRINGER", "IDM_SPRINGER_LEAD"]
  return idmSpringerRoles.includes(role)
}

export const isIdmOperationalRole = (role) => {
  const idmOperationalRoles = ["IDM_DISPONENT", "IDM_PROCUREMENT"]
  return idmOperationalRoles.includes(role)
}

export const isIdmUserOrLead = (role) => {
  const idmRoles = ["IDM_USER", "IDM_DISPONENT", "IDM_LEAD", "IDM_THEKE", "IDM_PROCUREMENT"]
  return idmRoles.includes(role)
}

export const isAdmLeadUser = (role) => {
  const admRoles = ["ADMIN", "REGIONAL_LEAD", "ADM_GVL"]
  return admRoles.includes(role)
}

export const isLeadAdminUser = (role) => {
  const leadRoles = ["ADMIN", "REGIONAL_LEAD", "IDM_LEAD", "IDM_SPRINGER_LEAD"]
  return leadRoles.includes(role)
}

export const validateMFA = async ({ email, signInToken, req, deviceId }) => {
  const ip =
    req.headers["x-forwarded-for"]?.split(",")[0] || // For reverse proxies like Vercel
    req.socket?.remoteAddress
  const userAgent = req.headers["user-agent"]

  if (Authsignal.isMFAIgnored(ip)) return

  const loginAuthsignalChallenge = await Authsignal.loginChallengeValidation({
    token: signInToken,
    userId: email,
    ip,
    email,
    deviceId,
    userAgent,
  })

  if (!loginAuthsignalChallenge.isValid) {
    throw new Error(loginAuthsignalChallenge.message)
  }
}

/** @type {import("next-auth").AuthOptions} */
export const authOptions = {
  session: {
    strategy: "jwt",
    maxAge: SESSION_MAX_AGE, // 12 hours
  },
  providers: [
    CredentialsProvider({
      name: "Credentials",
      async authorize(credentials, req) {
        const { email, password, signInToken, deviceId } = credentials

        await validateMFA({ email: email.toLowerCase(), signInToken, req, deviceId })

        const { ERROR_MSG_EMAIL_INVALID, ERROR_MSG_REQUIRED, ERROR_CREDENTIALS } = getLocaleStrings()

        const errorMessages = {
          email: ERROR_MSG_EMAIL_INVALID,
          required: ERROR_MSG_REQUIRED,
        }

        // validate inputs
        try {
          await getSignInSchema(errorMessages).validate({ email, password })
        } catch (err) {
          throw new Error(err)
        }

        const client = getGqlClient()

        const { data = null } = await client.mutate({
          mutation: gql`
            mutation SignInUser($email: EmailAddress, $password: String) {
              signInUser(email: $email, password: $password) {
                id
                firstName
                lastName
                customerGroup
                role
                employeeNumber
                branchId
                customAdmFields {
                  admNumbers
                  regionalLeadNumbers
                  allowReservationMutation
                  allowAllCustomerSearch
                  allowDocumentMutation
                  enableAssistant
                  altBranchCustomerSearch
                }
              }
            }
          `,
          variables: { email: email, password: password },
        })
        if (!data?.signInUser?.id) {
          throw new Error(ERROR_CREDENTIALS)
        }

        const employeeBranchId = data?.signInUser?.branchId || ""
        let branchId = ""

        if (isIdmUser(data.signInUser.role)) {
          branchId = employeeBranchId
        }

        await setAdmCookies({ admNumber: data?.signInUser?.customAdmFields?.admNumbers[0] })
        await setCustomerCookies({
          customerId: "",
          customerNumber: "",
          customerName: "",
          branchId,
          employeeBranchId,
        })
        const { firstName, lastName, customerGroup, customAdmFields } = data.signInUser

        return {
          id: data.signInUser.id,
          email,
          name: `${firstName} ${lastName}`,
          firstName,
          lastName,
          admNumbers: customAdmFields?.admNumbers,
          regionalLeadNumbers: customAdmFields?.regionalLeadNumbers,
          role: data.signInUser.role,
          employeeNumber: data.signInUser.employeeNumber,
          allowReservationMutation: customAdmFields?.allowReservationMutation ?? false,
          allowAllCustomerSearch: customAdmFields?.allowAllCustomerSearch ?? false,
          allowDocumentMutation: customAdmFields?.allowDocumentMutation ?? false,
          enableAssistant: customAdmFields?.enableAssistant ?? false,
          altBranchCustomerSearch: customAdmFields?.altBranchCustomerSearch ?? false,
          customerGroup: {
            id: customerGroup?.obj?.id ?? null,
            key: customerGroup?.obj?.key ?? null,
            name: customerGroup?.obj?.name ?? null,
          },
        }
      },
    }),
  ],
  secret: process.env.NEXTAUTH_SECRET,
  callbacks: {
    async jwt({ token, user }) {
      return !user
        ? token
        : {
            ...token,
            firstName: user.firstName,
            lastName: user.lastName,
            customerGroup: user.customerGroup,
            admNumbers: user.admNumbers,
            regionalLeadNumbers: user?.regionalLeadNumbers,
            role: user.role,
            employeeNumber: user.employeeNumber,
            allowReservationMutation: user?.allowReservationMutation ?? false,
            allowAllCustomerSearch: user?.allowAllCustomerSearch ?? false,
            allowDocumentMutation: user?.allowDocumentMutation ?? false,
            enableAssistant: user?.enableAssistant ?? false,
            altBranchCustomerSearch: user?.altBranchCustomerSearch ?? false,
          }
    },
    async session({ session, token }) {
      return !token
        ? session
        : {
            ...session,
            user: {
              ...session.user,
              id: token.sub,
              firstName: token.firstName,
              lastName: token.lastName,
              customerGroup: token.customerGroup,
              admNumbers: token.admNumbers,
              regionalLeadNumbers: token.regionalLeadNumbers,
              role: token.role,
              employeeNumber: token.employeeNumber,
              allowReservationMutation: token?.allowReservationMutation ?? false,
              allowAllCustomerSearch: token?.allowAllCustomerSearch ?? false,
              allowDocumentMutation: token?.allowDocumentMutation ?? false,
              enableAssistant: token?.enableAssistant ?? false,
              altBranchCustomerSearch: token?.altBranchCustomerSearch ?? false,
            },
          }
    },
  },
  pages: {
    signIn: "/auth/signin",
  },
}
